import { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  IconButton,
  Alert,
  Snackbar,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Autocomplete,
  Chip,
  TextField,
  useMediaQuery,
  useTheme,
  type SelectChangeEvent,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useAuth } from '../../contexts/AuthContext';
import { findUserByEmail, getUsersByIds, type UserInfo } from '../../services/users';
import { shareItem, unshareItem } from '../../services/items';
import { updateFolder } from '../../services/folders';
import { listContacts, createContact, getContactDisplayName, getContactInitials } from '../../services/contacts';
import type { Contact, FolderRole } from '../../types';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface ShareDialogProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  // For items
  itemId?: string;
  currentSharedWith?: string[];
  // For folders
  folderId?: string;
  currentMembers?: Record<string, FolderRole>;
  ownerUid?: string;
  // Common
  onUpdated: () => void;
}

export default function ShareDialog({
  open,
  onClose,
  title,
  itemId,
  currentSharedWith,
  folderId,
  currentMembers,
  ownerUid,
  onUpdated,
}: ShareDialogProps) {
  const { user } = useAuth();
  const [emails, setEmails] = useState<string[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [role, setRole] = useState<FolderRole>('viewer');
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');
  const [loading, setLoading] = useState(false);
  const [sharedUsers, setSharedUsers] = useState<UserInfo[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const contactsFetchedRef = useRef(false);

  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isFolder = !!folderId;

  // Load shared user info + contacts when dialog opens
  useEffect(() => {
    if (!open) {
      contactsFetchedRef.current = false;
      return;
    }
    setEmails([]);
    setInputValue('');
    setError('');
    setContacts([]);

    const loadUsers = async () => {
      setLoadingUsers(true);
      try {
        if (isFolder && currentMembers) {
          const userIds = Object.keys(currentMembers).filter((uid) => uid !== ownerUid);
          const users = await getUsersByIds(userIds);
          setSharedUsers(users);
        } else if (currentSharedWith?.length) {
          // currentSharedWith contains emails for items
          const users = (await Promise.all(
            currentSharedWith.map((email) => findUserByEmail(email)),
          )).filter((u): u is UserInfo => u !== null);
          setSharedUsers(users);
        }
      } finally {
        setLoadingUsers(false);
      }
    };
    loadUsers();

    // Load contacts for autocomplete
    if (user && !contactsFetchedRef.current) {
      contactsFetchedRef.current = true;

      listContacts().then((result) => {
        setContacts(result);
      });

    }
  }, [open, currentSharedWith, currentMembers, ownerUid, isFolder, user]);

  const addChip = (raw: string) => {
    const trimmed = raw.replace(/[,\s]+$/, '').trim();
    if (trimmed && EMAIL_RE.test(trimmed) && !emails.includes(trimmed)) {
      setEmails((prev) => [...prev, trimmed]);
      return true;
    }
    return false;
  };

  const handleShare = async () => {
    // Confirm anything still in the input box
    const pending = inputValue.trim();
    let finalEmails = [...emails];
    if (pending && EMAIL_RE.test(pending) && !finalEmails.includes(pending)) {
      finalEmails.push(pending);
    }

    if (finalEmails.length === 0) return;
    setError('');
    setToast('');
    setLoading(true);

    try {
      if (isFolder && folderId && currentMembers) {
        // Folder sharing: add members by UID
        for (const emailAddr of finalEmails) {
          const foundUser = await findUserByEmail(emailAddr);
          if (foundUser && !currentMembers[foundUser.uid]) {
            await updateFolder(folderId, {
              members: { ...currentMembers, [foundUser.uid]: role },
            });
          }
        }
      } else if (itemId) {
        // Item sharing: use the backend share endpoint (handles DB, email, history)
        const senderName = user?.displayName || 'Someone';
        await shareItem(itemId, finalEmails, senderName, title);
      }

      // Look up users for the shared-with list UI
      for (const emailAddr of finalEmails) {
        const foundUser = await findUserByEmail(emailAddr);
        if (foundUser) {
          setSharedUsers((prev) =>
            prev.some((u) => u.uid === foundUser.uid) ? prev : [...prev, foundUser],
          );
        }
      }

      // Auto-create contacts for new emails
      for (const emailAddr of finalEmails) {
        if (!contacts.find(c => c.email === emailAddr)) {
          try {
            await createContact({ email: emailAddr });
          } catch {
            // best-effort
          }
        }
      }

      setToast(`Shared with ${finalEmails.join(', ')}`);
      setEmails([]);
      setInputValue('');
      onUpdated();
      onClose();
    } catch (err) {
      console.error('Share failed:', err);
      setError('Failed to share. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveUser = async (uid: string) => {
    try {
      if (isFolder && folderId && currentMembers) {
        const updated = { ...currentMembers };
        delete updated[uid];
        await updateFolder(folderId, { members: updated });
      } else if (itemId) {
        const userToRemove = sharedUsers.find((u) => u.uid === uid);
        if (userToRemove) {
          const allEmails = sharedUsers.map((u) => u.email);
          await unshareItem(itemId, userToRemove.email, allEmails);
        }
      }
      setSharedUsers((prev) => prev.filter((u) => u.uid !== uid));
      onUpdated();
    } catch {
      setError('Failed to remove user. Please try again.');
    }
  };

  const getMemberRole = (uid: string): string => {
    if (!currentMembers) return '';
    return currentMembers[uid] ?? '';
  };

  // Filter out already-added emails from autocomplete options
  const filteredOptions = contacts.filter((c) => c.email && !emails.includes(c.email));

  return (
    <>
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={mobile}>
      <DialogTitle>
        Share {isFolder ? 'Folder' : 'Item'}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', gap: 1, mt: 1, alignItems: 'flex-start' }}>
          <Autocomplete<Contact | string, true, false, true>
            multiple
            freeSolo
            options={filteredOptions}
            getOptionLabel={(option) =>
              typeof option === 'string' ? option : option.email || ''
            }
            filterOptions={(options, state) => {
              if (state.inputValue.length === 0) return [];
              const q = state.inputValue.toLowerCase();
              return options.filter((c) =>
                (typeof c === 'string' ? c.toLowerCase().includes(q) :
                  c.email?.toLowerCase().includes(q) ||
                  c.firstName?.toLowerCase().includes(q) ||
                  c.lastName?.toLowerCase().includes(q) ||
                  c.nicknames.some(n => n.toLowerCase().includes(q)))
              ).slice(0, 10);
            }}
            renderOption={(props, contact) => {
              if (typeof contact === 'string') return <li {...props}>{contact}</li>;
              const { key, ...rest } = props;
              return (
                <li key={key} {...rest}>
                  <Avatar src={contact.photoUrl || undefined} sx={{ width: 28, height: 28, mr: 1 }}>
                    {getContactInitials(contact)}
                  </Avatar>
                  <Box>
                    <Typography variant="body2">{getContactDisplayName(contact)}</Typography>
                    <Typography variant="caption" color="text.secondary">{contact.email}</Typography>
                  </Box>
                </li>
              );
            }}
            value={emails}
            inputValue={inputValue}
            onChange={(_event, newValue) => {
              const validated = newValue
                .map(v => typeof v === 'string' ? v.trim() : v.email)
                .filter((v): v is string => !!v && EMAIL_RE.test(v));
              setEmails(validated);
              setInputValue('');
              setError('');
              setToast('');
            }}
            onInputChange={(_event, newInput, reason) => {
              if (reason === 'reset') {
                setInputValue('');
                return;
              }
              // Comma or space → confirm chip
              if (newInput && /[,\s]$/.test(newInput)) {
                if (addChip(newInput)) {
                  setInputValue('');
                  return;
                }
              }
              setInputValue(newInput);
              setError('');
              setToast('');
            }}
            renderTags={(value, getTagProps) =>
              value.map((item, index) => {
                const { key, ...rest } = getTagProps({ index });
                const emailStr = typeof item === 'string' ? item : item.email || '';
                const contact = contacts.find(c => c.email === emailStr);
                return (
                  <Chip
                    key={key}
                    avatar={contact ? <Avatar src={contact.photoUrl || undefined} sx={{ width: 20, height: 20 }}>{getContactInitials(contact)}</Avatar> : undefined}
                    label={contact ? getContactDisplayName(contact) : emailStr}
                    size="small"
                    {...rest}
                  />
                );
              })
            }
            renderInput={(params) => (
              <TextField
                {...params}
                autoFocus
                label="Email addresses"
                placeholder={emails.length === 0 ? 'name@example.com' : ''}
                size="small"
              />
            )}
            fullWidth
            sx={{ flex: 1 }}
          />
          {isFolder && (
            <FormControl size="small" sx={{ minWidth: 100 }}>
              <InputLabel>Role</InputLabel>
              <Select
                value={role}
                label="Role"
                onChange={(e: SelectChangeEvent) => setRole(e.target.value as FolderRole)}
              >
                <MenuItem value="editor">Editor</MenuItem>
                <MenuItem value="viewer">Viewer</MenuItem>
              </Select>
            </FormControl>
          )}
          <Button
            variant="contained"
            onClick={handleShare}
            disabled={loading || (emails.length === 0 && !inputValue.trim())}
            sx={{ whiteSpace: 'nowrap' }}
          >
            {loading ? <CircularProgress size={22} /> : 'Share'}
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {error}
          </Alert>
        )}

        {loadingUsers ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : sharedUsers.length > 0 ? (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Shared with
            </Typography>
            <List dense>
              {sharedUsers.map((u) => {
                const contact = contacts.find(c => c.email === u.email);
                const displayName = contact ? getContactDisplayName(contact) : (u.displayName || u.email);
                const photoUrl = contact?.photoUrl || u.photoURL;
                return (
                <ListItem
                  key={u.uid}
                  secondaryAction={
                    <IconButton
                      edge="end"
                      size="small"
                      onClick={() => handleRemoveUser(u.uid)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  }
                >
                  <ListItemAvatar>
                    <Avatar src={photoUrl} sx={{ width: 32, height: 32 }}>
                      {displayName.charAt(0).toUpperCase()}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={displayName}
                    secondary={
                      isFolder
                        ? `${u.email} - ${getMemberRole(u.uid)}`
                        : u.email
                    }
                  />
                </ListItem>
                );
              })}
            </List>
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Not shared with anyone yet.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
    <Snackbar
      open={!!toast}
      autoHideDuration={4000}
      onClose={() => setToast('')}
      message={toast}
    />
    </>
  );
}
